import { StyleSheet, Text, View, ViewProps } from 'react-native'

import { Icon } from '../../icons'
import { tokens } from '../../theme/tokens'

export interface OrderInfoHeaderProps extends ViewProps {
  /** Client display name shown as the heading (resolved by the caller). */
  clientName: string
  trackingId: string
  /** Label prefix for the tracking line. Pass a translated string. */
  trackingLabel?: string
}

/**
 * Order summary header: package icon, client name, and tracking id.
 *
 * Extracted from CWA's `OrderInfoHeader`. The original read `order` from
 * `AppContext`; here the caller resolves the order (and the null case) and
 * passes plain strings, keeping this a pure renderer.
 */
export const OrderInfoHeader = ({
  clientName,
  trackingId,
  trackingLabel = 'Tracking ID',
  style,
  ...passthroughProps
}: OrderInfoHeaderProps) => {
  return (
    <View {...passthroughProps} style={[styles.container, style]}>
      <View style={styles.row}>
        <View style={styles.identity}>
          <View style={styles.iconBox}>
            <Icon name="package" size={24} />
          </View>
          <View>
            {/* The deleted CWA component rendered this as a Heading; keep the
                document-outline semantics (react-native-web emits <h2>). */}
            <Text accessibilityRole="header" aria-level={2} style={styles.clientName}>
              {clientName}
            </Text>
            <Text style={styles.trackingId}>
              {trackingLabel} {trackingId}
            </Text>
          </View>
        </View>
      </View>
    </View>
  )
}

OrderInfoHeader.displayName = 'OrderInfoHeader'

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingBottom: 24,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomColor: tokens.colors.brand.textPlaceholder,
    borderBottomWidth: 1,
    paddingBottom: 8,
    paddingLeft: 8,
  },
  identity: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconBox: {
    backgroundColor: tokens.colors.brand.lightGrey,
    padding: 8,
    height: 36,
    marginRight: 8,
    borderRadius: 8,
  },
  clientName: {
    fontSize: 16,
    fontWeight: '700',
    color: tokens.colors.brand.textSoftBlack,
  },
  trackingId: {
    fontSize: 12,
    color: tokens.colors.brand.secondaryGrey,
  },
})
